import React, { useState } from 'react';
import axios from 'axios';
import './Redownload.css';
import { buildDownloadUrl } from './downloadUrl';

function Redownload() {
  const [orderId, setOrderId] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [filePath, setFilePath] = useState(null);
  const [purchaseId, setPurchaseId] = useState(null);
  const [licenseKey, setLicenseKey] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showOtpInput, setShowOtpInput] = useState(false);
  const [showLicenseInputs, setShowLicenseInputs] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [copyFeedback, setCopyFeedback] = useState('');

  const handleRequestOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/request-otp`, { orderId, email });
      if (response.data.success) {
        setSuccess(response.data.message);
        setShowOtpInput(true);
      } else {
        setError('Invalid Order ID or email');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error requesting OTP. Please check your Order ID and email or contact support.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/redownload`, { orderId, email, otp });
      if (response.data.success) {
        setFilePath(response.data.filePath);
        setPurchaseId(response.data.purchaseId);
        setShowOtpInput(false);
        setOtp('');
        setShowLicenseInputs(true);
        setSuccess('Product ready for download. Generate your license below.');
      } else {
        setError('Invalid or expired OTP');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Error verifying OTP. Please try again or contact support.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGenerateLicense = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/licenses`, {
        purchaseId,
        email,
      });
      if (response.data.success) {
        setLicenseKey(response.data.licenseKey);
        setSuccess('License generated successfully. Copy your key and use it in your EA or indicator.');
        setShowLicenseInputs(false);
        setError('');
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to generate license');
      setSuccess('');
    }
  };

  const handleCopyLicenseKey = () => {
    if (licenseKey) {
      navigator.clipboard.writeText(licenseKey).then(() => {
        setCopyFeedback('Copied!');
        setTimeout(() => setCopyFeedback(''), 2000);
      }).catch(() => {
        setCopyFeedback('Failed to copy.');
        setTimeout(() => setCopyFeedback(''), 2000);
      });
    }
  };

  return (
    <div className="redownload page-shell">
      <div className="section-card">
        <span className="tag">Re-download</span>
        <h1 className="page-title">Retrieve your purchase</h1>
        <p className="page-lead">
          Enter your PayPal Order ID and purchase email. We will send a one-time password to verify your request.
        </p>

        {!filePath && (
          <form onSubmit={showOtpInput ? handleVerifyOtp : handleRequestOtp} className="form-grid">
            <input
              type="text"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="Order ID"
              required
              disabled={showOtpInput || isLoading}
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Purchase email"
              required
              disabled={showOtpInput || isLoading}
            />
            {showOtpInput && (
              <input
                type="text"
                value={otp}
                onChange={(e) => setOtp(e.target.value)}
                placeholder="OTP"
                required
                disabled={isLoading}
              />
            )}
            <button type="submit" disabled={isLoading}>
              {isLoading ? 'Processing...' : showOtpInput ? 'Verify OTP' : 'Request OTP'}
            </button>
          </form>
        )}
        {error && <p className="error-text">{error}</p>}
        {success && <p className="success-text">{success}</p>}

        {filePath && (
          <div className="download-block">
            <a href={buildDownloadUrl(filePath)} download className="primary-cta">
              Download Again
            </a>
          </div>
        )}
      </div>

      {filePath && (
        <div className="section-card">
          <h2>Generate license key</h2>
          <p className="muted-text">
            Licenses are limited to two devices per product. Your EA/indicator will bind the license automatically.
          </p>
          {showLicenseInputs && (
            <form onSubmit={handleGenerateLicense} className="form-grid">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Purchase email"
                required
                disabled
              />
              <button type="submit">Generate License</button>
            </form>
          )}
          {licenseKey && (
            <>
              <div className="license-key-container">
                <h3>Your license key</h3>
                <div className="license-key-box">{licenseKey}</div>
                <button onClick={handleCopyLicenseKey} type="button">
                  {copyFeedback || 'Copy License Key'}
                </button>
              </div>

              <div className="activation-panel">
                <h3>How to activate in MT5</h3>
                <ol className="activation-steps">
                  <li>Download the product file above and install it in the correct MT5 folder.</li>
                  <li>In MT5, go to <strong>Tools &gt; Options &gt; Expert Advisors</strong>.</li>
                  <li>Enable <strong>Allow WebRequest for listed URL</strong>.</li>
                  <li>Add this URL exactly: <strong>https://www.vixalgotrading.com</strong></li>
                  <li>Attach the EA or indicator to a chart.</li>
                  <li>Paste your key into the <strong>LicenseKey</strong> input field.</li>
                  <li>Click <strong>OK</strong> and allow the product to verify online.</li>
                </ol>
                <p className="muted-text">
                  This license is valid for this product only and can be used on up to two MT5 installations/devices.
                </p>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default Redownload;
